import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNotificationStore } from '../../../components/notificationStore';
import { useUserStore } from '../../../components/userStore';
import SKURow from './SKURow';

const SKUModal = ({
	isOpen = false,
	onCancel,
	exServiceId,
	serviceName = '',
}) => {
	const modalRef = useRef();
	const { user } = useUserStore();
	const [skus, setSKUs] = useState([]);
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationStore();
	const skusQuery = useQuery({
		queryKey: ['service-skus', user.coord_agency.id, exServiceId],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/service-skus/${exServiceId}`,
			);
			return data;
		},
		enabled: false,
	});
	const skusMutation = useMutation({
		mutationFn: async () => {
			const { data } = await axios.post(
				`/coord_agencies/${user.coord_agency.id}/service-skus/${exServiceId}`,
				{
					skus: skus.map((sku) => ({
						...sku,
						external_211_services_id: exServiceId,
					})),
				},
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['service-skus', user.coord_agency.id, exServiceId],
			});
			showSuccess('SKUs Updated');
			onCancel();
		},
		onError: () => {
			showError('There was a problem updating SKUs. Please try again.');
		},
	});
	useEffect(() => {
		if (isOpen) {
			modalRef.current.showModal();
			skusQuery.refetch();
		} else {
			queryClient.invalidateQueries({
				queryKey: ['service-skus', user.coord_agency.id, exServiceId],
			});
			setSKUs([]);
		}
	}, [isOpen, user.coord_agency.id, exServiceId]);
	useEffect(() => {
		if (skusQuery.isFetched && !skusQuery.isFetching) {
			setSKUs(skusQuery.data);
		}
	}, [skusQuery.isFetched, skusQuery.isFetching]);

	const handleAddSKU = () => {
		const newSKUs = [
			...skus,
			{
				id: `sku-${skus.length}`,
				external_211_services_id: exServiceId,
			},
		];
		setSKUs(newSKUs);
	};

	const handleUpdateSKU = (index, sku) => {
		const newSKUs = skus.slice();
		newSKUs[index] = sku;
		setSKUs(newSKUs);
	};

	const handleRemoveSKU = (index) => {
		const newSKUs = skus.slice();
		newSKUs.splice(index, 1);
		setSKUs(newSKUs);
	};

	const handleSaveSKUs = () => {
		skusMutation.mutate();
	};

	return (
		<dialog id="sku-form-dialog" className="modal modal-top" ref={modalRef}>
			<div className="modal-box">
				<div className="flex flex-row place-content-between">
					<h3>SKUs for {serviceName}</h3>
					<button className="btn btn-primary btn-sm" onClick={handleAddSKU}>
						Add SKU
					</button>
				</div>
				<div className="grid grid-cols-7 gap-2">
					{skus.map((sku, index) => (
						<SKURow
							key={`${sku.id}`}
							sku={sku}
							hideLabels={index > 0}
							onSKUChange={(sku) => handleUpdateSKU(index, sku)}
							onDelete={() => handleRemoveSKU(index)}
						/>
					))}
					{skus.length === 0 && <div>No SKUs found</div>}
				</div>
				<div className="modal-action">
					<form method="dialog">
						<button
							className="btn"
							onClick={onCancel}
							disabled={skusMutation.isPending}
						>
							Cancel
						</button>
						<button
							className="ml-4 btn btn-primary"
							onClick={handleSaveSKUs}
							disabled={skusMutation.isPending}
						>
							Save
						</button>
					</form>
				</div>
			</div>
		</dialog>
	);
};

SKUModal.propTypes = {
	isOpen: PropTypes.bool,
	onCancel: PropTypes.func,
	exServiceId: PropTypes.number,
	serviceName: PropTypes.string,
};

export default SKUModal;
